/**
 * Listes de styles nommés d'une `configuration` (`tableStyles`,
 * `infoCardStyles`…) : ce que les onglets de styles du classique et le thème
 * d'un site en font, une seule fois (cf. docs/architecture-logique-ui.md).
 *
 * Chaque opération renvoie un patch de configuration : le classique le passe
 * à `setConfiguration`, le site le fusionne dans `theme.configuration`.
 * Seuls les styles enregistrés sont touchés : ceux des sections liées
 * (`fromTmp`) ne s'affichent que fusionnés, ils ne s'éditent pas.
 */

/** Les clés de `configuration` d'une liste : la liste, son compteur, son Défaut. */
export function styleListKeys(list) {
  return { list, idCount: `${list}IdCount`, defaultId: `${list}DefaultId` };
}

const stylesOf = (configuration, list) => configuration?.[list] || [];

/**
 * Le prochain identifiant : le compteur, sans jamais reprendre un id déjà
 * porté — un compteur absent (template d'avant la liste) ou en retard ne doit
 * pas produire deux styles de même id.
 */
export function nextStyleId(configuration, list) {
  const count = Number(configuration?.[styleListKeys(list).idCount]) || 0;
  const numericIds = stylesOf(configuration, list)
    .map((style) => style.id)
    .filter((id) => Number.isInteger(id));
  return Math.max(count, ...numericIds.map((id) => id + 1));
}

/** @returns {{ patch: object, style: object }} le style créé, avec son id */
export function addStyle(configuration, list, style) {
  const id = nextStyleId(configuration, list);
  const added = { ...style, id };
  return {
    patch: {
      [list]: [...stylesOf(configuration, list), added],
      [styleListKeys(list).idCount]: id + 1,
    },
    style: added,
  };
}

export function copyStyle(configuration, list, id) {
  const copied = stylesOf(configuration, list).find((style) => style.id === id);
  return addStyle(configuration, list, { ...copied, name: `${copied.name} - Copie` });
}

export function updateStyle(configuration, list, updated) {
  return {
    [list]: stylesOf(configuration, list)
      .map((style) => (style.id === updated.id ? updated : style)),
  };
}

export function renameStyle(configuration, list, id, name) {
  const style = stylesOf(configuration, list).find((candidate) => candidate.id === id);
  return updateStyle(configuration, list, { ...style, name });
}

export function removeStyle(configuration, list, id) {
  return { [list]: stylesOf(configuration, list).filter((style) => style.id !== id) };
}

export function pinDefaultStyle(list, id) {
  return { [styleListKeys(list).defaultId]: id };
}

/**
 * Ce qu'on peut faire d'un style dans la liste. Dans un document, le style 0
 * est celui des éléments sans style : il ne se renomme ni ne se supprime. Un
 * site n'impose aucun style, son id 0 est un style comme un autre. Un style
 * de section liée ne s'édite pas ici.
 *
 * @param {{ site?: boolean }} scope
 */
export function styleLocks(style, { site = false } = {}) {
  const linked = style?.fromTmp === true;
  const fixed = linked || (!site && style?.id === 0);
  return { rename: fixed, remove: fixed, copy: linked };
}
